import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Search, 
  MapPin, 
  Clock, 
  Star,
  Phone,
  ChevronRight,
  Filter,
  Pill
} from 'lucide-react';

// Mock pharmacies data
const getMockPharmacies = (lang) => [
  {
    id: 1,
    name: lang === 'ar' ? 'صيدلية الشفاء' : 'Al-Shifa Pharmacy',
    address: lang === 'ar' ? 'شارع المزة، دمشق' : 'Mazzeh Street, Damascus',
    phone: '+963 11 123 4567',
    rating: 4.8,
    reviews: 256,
    image: 'https://images.unsplash.com/photo-1586015555751-63bb77f4322a?w=400&h=300&fit=crop',
    isOpen: true,
    hours: lang === 'ar' ? '8 ص - 11 م' : '8AM - 11PM',
    delivery: true,
    distance: '1.2 km',
  },
  {
    id: 2,
    name: lang === 'ar' ? 'صيدلية الحياة' : 'Al-Hayat Pharmacy',
    address: lang === 'ar' ? 'شارع بغداد، دمشق' : 'Baghdad Street, Damascus',
    phone: '+963 11 234 5678',
    rating: 4.6,
    reviews: 189,
    image: 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=400&h=300&fit=crop',
    isOpen: true,
    hours: lang === 'ar' ? '24 ساعة' : '24 Hours',
    delivery: true,
    distance: '2.5 km',
  },
  {
    id: 3,
    name: lang === 'ar' ? 'صيدلية الأمل' : 'Al-Amal Pharmacy',
    address: lang === 'ar' ? 'شارع الثورة، دمشق' : 'Thawra Street, Damascus',
    phone: '+963 11 345 6789',
    rating: 4.5,
    reviews: 142,
    image: 'https://images.unsplash.com/photo-1631549916768-4119b2e5f926?w=400&h=300&fit=crop',
    isOpen: false,
    hours: lang === 'ar' ? '9 ص - 9 م' : '9AM - 9PM',
    delivery: false,
    distance: '3.8 km',
  },
  {
    id: 4,
    name: lang === 'ar' ? 'صيدلية النور' : 'Al-Nour Pharmacy',
    address: lang === 'ar' ? 'شارع الحمراء، دمشق' : 'Hamra Street, Damascus',
    phone: '+963 11 456 7890',
    rating: 4.9,
    reviews: 312,
    image: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=400&h=300&fit=crop',
    isOpen: true,
    hours: lang === 'ar' ? '7 ص - 12 م' : '7AM - 12AM',
    delivery: true,
    distance: '0.8 km',
  },
];

const PharmacyListPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setTimeout(() => {
      setPharmacies(getMockPharmacies(lang));
      setLoading(false);
    }, 500);
  }, [lang]);

  const filteredPharmacies = pharmacies.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="pharmacy-list-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-4">
            <Pill className="h-8 w-8" />
            <h1 className="text-2xl font-bold">
              {lang === 'ar' ? 'الصيدليات' : 'Pharmacies'}
            </h1>
          </div>
          <p className="text-green-100 mb-6">
            {lang === 'ar' ? 'ابحث عن الصيدليات القريبة منك' : 'Find pharmacies near you'}
          </p>
          
          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder={lang === 'ar' ? 'ابحث عن صيدلية...' : 'Search pharmacies...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white text-slate-900 dark:text-white"
              data-testid="search-pharmacies"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm overflow-hidden">
                <Skeleton className="h-40 w-full" />
                <CardContent className="p-4">
                  <Skeleton className="h-5 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredPharmacies.length === 0 ? (
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="p-8 text-center">
              <Pill className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {lang === 'ar' ? 'لم يتم العثور على صيدليات' : 'No pharmacies found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPharmacies.map(pharmacy => (
              <Link key={pharmacy.id} to={`/pharmacy/${pharmacy.id}`}>
                <Card className="dark:bg-slate-800 border-0 shadow-sm overflow-hidden hover:shadow-md transition-shadow h-full">
                  <div className="relative h-40">
                    <img src={pharmacy.image} alt={pharmacy.name} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <Badge className={pharmacy.isOpen ? 'bg-green-500' : 'bg-red-500'}>
                        {pharmacy.isOpen ? (lang === 'ar' ? 'مفتوح' : 'Open') : (lang === 'ar' ? 'مغلق' : 'Closed')}
                      </Badge>
                      {pharmacy.delivery && (
                        <Badge className="bg-blue-500">
                          {lang === 'ar' ? 'توصيل' : 'Delivery'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-slate-900 dark:text-white">{pharmacy.name}</h3>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="text-sm font-medium">{pharmacy.rating}</span>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-slate-500 dark:text-slate-400">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span className="truncate">{pharmacy.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>{pharmacy.hours}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-sm text-slate-400">{pharmacy.distance}</span>
                      <Button variant="ghost" size="sm" className="text-green-600">
                        {lang === 'ar' ? 'عرض' : 'View'}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PharmacyListPage;
